/**
 * processReadonlyRenderer.tsx — renderer pól WYLICZANYCH procesu (aktywny proces /
 * bieżący etap) na formularzu kartoteki, ZAWSZE tylko-do-odczytu (nawet w trybie
 * edycji rekordu — decyzja produktowa). Pola nie mają fizycznej kolumny ani wpisu w
 * katalogu `system_module_fields` — wartość przychodzi z `extra` (strona pobiera ją z
 * `GET /api/processes/instances` i wstrzykuje). Patrz `lib/processes/computed-fields.ts`
 * + ADR-0093.
 *
 * Osobny plik `.tsx`, bo `invoiceEngineConfig.ts` jest bez JSX (renderery delegowane).
 * Generyczny (nie zależy od TForm) — gotowy do reużycia przy migracji kolejnych klas
 * dokumentów na silnik list.
 */

import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';

import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type { RecordInstanceListingRow } from '@/lib/processes/instance-api';
import { finalNodeLabelOrNull } from '@/lib/processes/step-tone';

/** Wartości procesu wstrzykiwane przez stronę do `FieldRenderContext.extra`. */
export interface ProcessReadonlyExtra {
  /**
   * Nazwa procesu sprawy rekordu (w toku, zakończonej albo z błędem); `null`/pusta, gdy rekord
   * jest „bez procesu" — zasady w `deriveProcessReadonlyExtra`.
   */
  activeProcessName?: string | null;
  /**
   * Etykiety bieżących etapów sprawy w toku (może być kilka — gałęzie równoległe); przy sprawie
   * zakończonej — jeden etap końcowy.
   */
  currentStage?: string[];
}

/**
 * Wyprowadza wartości readonly pól procesu z listy instancji rekordu
 * (`GET /api/processes/instances`) — tak samo jak kolumny „Aktywny proces" / „Bieżący etap" na
 * liście. „Sprawa rekordu" = najnowsza NIEANULOWANA sprawa (najwyższy numer sprawy); anulowane są
 * pomijane, więc rekord bez innej sprawy nie ma ani procesu, ani etapu.
 *
 *  - w toku (`active` z bieżącymi etapami) → nazwa procesu + etykiety bieżących etapów,
 *  - zakończona (`completed`) → nazwa procesu + etap końcowy (sprawa po dojściu do końca STOI na
 *    nim); brak nazwy etapu końcowego → sam proces, bez etapu,
 *  - błąd (`error`) → nazwa procesu, bez etapu,
 *  - brak sprawy, same anulowane, inny status (nieużywany `suspended`) albo sprawa w toku bez
 *    bieżących etapów → brak wartości — jak kolumny listy i prawa na etapach, które taką sprawę
 *    traktują jako „bez procesu".
 *
 * Wyjątek względem listy: bez prawa podglądu spraw (`processes.instances.view`) w firmie rekordu
 * albo bez prawa podglądu samego rekordu serwer zwraca tylko sprawy, w których pytający może TERAZ
 * decydować — w pozostałych przypadkach pola pokażą „—", a kolumny listy (bez własnej bramki
 * uprawnień) pokażą proces.
 *
 * Współdzielone przez karty faktur (DRY, testowalne).
 */
export function deriveProcessReadonlyExtra(
  instances: RecordInstanceListingRow[] | undefined,
): ProcessReadonlyExtra {
  let latest: RecordInstanceListingRow | null = null;
  for (const row of instances ?? []) {
    if (row.status === 'cancelled') continue;
    if (latest === null || row.instanceNumber > latest.instanceNumber) latest = row;
  }
  if (latest === null) return { activeProcessName: null, currentStage: [] };

  const activeProcessName = latest.processName ?? null;
  switch (latest.status) {
    case 'completed': {
      const finalLabel = finalNodeLabelOrNull(latest.finalNodeLabel);
      return { activeProcessName, currentStage: finalLabel === null ? [] : [finalLabel] };
    }
    case 'error':
      return { activeProcessName, currentStage: [] };
    case 'active':
      // Sprawa w toku bez bieżących etapów nie stoi na żadnym etapie — „bez procesu".
      if ((latest.currentNodeIds ?? []).length === 0) {
        return { activeProcessName: null, currentStage: [] };
      }
      return {
        activeProcessName,
        currentStage: latest.currentNodeLabels.filter((l): l is string => l !== null),
      };
    default:
      return { activeProcessName: null, currentStage: [] };
  }
}

/**
 * Renderuje pole procesu jako tekst read-only w ramce (bez inputu, nic nie zapisuje).
 * `slug` rozróżnia, którą wartość z `extra` pokazać; `labelKey` = klucz i18n etykiety
 * (ten sam, co kolumna listy — `computed-fields.ts`). Brak wartości → „—".
 */
export function renderReadonlyProcessField(args: {
  slug: string;
  labelKey: string;
  extra: ProcessReadonlyExtra | undefined;
  testIdPrefix: string;
  t: TFunction;
}): ReactNode {
  const { slug, labelKey, extra, testIdPrefix, t } = args;

  const display =
    slug === 'current_stage'
      ? extra?.currentStage && extra.currentStage.length > 0
        ? extra.currentStage.join(', ')
        : '—'
      : extra?.activeProcessName && extra.activeProcessName.length > 0
        ? extra.activeProcessName
        : '—';

  return (
    <Field label={t(labelKey)} colSpan={1}>
      <div
        className="rounded border border-border-secondary bg-bg-tertiary px-2 py-1.5 text-sm text-text-secondary"
        data-testid={`${testIdPrefix}-${slug}`}
      >
        {display}
      </div>
    </Field>
  );
}
